/**
 * Os glifos do menu.
 *
 * Desenhados à mão, em traço de 1.5px e `currentColor`, porque a lateral
 * recolhida mostra **só** eles: em 3.5rem não cabe rótulo, e um ícone que só
 * funciona ao lado do texto deixa de funcionar exatamente onde é necessário.
 *
 * Nenhuma biblioteca: seis formas geométricas não pagam uma dependência, e o
 * vocabulário aqui é o de diagrama — retângulo, linha, nó —, não o de um
 * conjunto de ícones alegre que existe para outra coisa.
 *
 * `aria-hidden` em todos: o nome acessível do link é o rótulo em texto, que
 * continua no DOM mesmo recolhido. Ícone com rótulo próprio faria o leitor de
 * tela anunciar o nome duas vezes.
 */

export const CHAVES_DE_ICONE = [
  'formularios',
  'construtor',
  'respostas',
  'auditoria',
  'pessoas',
  'sair',
] as const;

export type ChaveDeIcone = (typeof CHAVES_DE_ICONE)[number];

const CAMINHOS: Readonly<Record<ChaveDeIcone, React.ReactNode>> = {
  // Uma pilha de fichas: a fila de formulários.
  formularios: (
    <>
      <rect x="2.5" y="3.5" width="11" height="4" rx="1" />
      <rect x="2.5" y="9.5" width="11" height="4" rx="1" />
    </>
  ),
  // Dois nós e a aresta ortogonal entre eles: o grafo, que é o produto.
  construtor: (
    <>
      <rect x="1.5" y="2.5" width="5" height="4" rx="1" />
      <rect x="9.5" y="9.5" width="5" height="4" rx="1" />
      <path d="M4 6.5v3.5a1 1 0 0 0 1 1h4.5" />
    </>
  ),
  // Linhas de tabela, uma mais curta: a fila densa.
  respostas: (
    <>
      <path d="M2.5 4h11M2.5 8h11M2.5 12h7" />
    </>
  ),
  // Relógio: a trilha é o tempo.
  auditoria: (
    <>
      <circle cx="8" cy="8" r="5.5" />
      <path d="M8 4.75V8l2.25 1.5" />
    </>
  ),
  // Duas pessoas.
  pessoas: (
    <>
      <circle cx="6" cy="5.5" r="2.5" />
      <path d="M1.75 13.25a4.25 4.25 0 0 1 8.5 0" />
      <path d="M10.75 3.5a2.5 2.5 0 0 1 0 4.9M11.5 9.5a4.25 4.25 0 0 1 2.75 3.75" />
    </>
  ),
  // Porta com seta para fora.
  sair: (
    <>
      <path d="M6.5 2.5h-3a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1h3" />
      <path d="M10 5.5 12.5 8 10 10.5M12.5 8H6" />
    </>
  ),
};

export function Icone({ chave }: { chave: ChaveDeIcone }) {
  return (
    <svg
      className="menu__icone"
      viewBox="0 0 16 16"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {CAMINHOS[chave]}
    </svg>
  );
}
